import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";

import { orderService } from "/app/src/services";

import { useTranslation } from "react-i18next";
import { Order as OrderType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
} from "@heroui/react";
import useSetting from "/app/src/components/settings/setting";
import {
  isColumnSelected,
  toggleExplorerColumn,
  buildExplorerColumns,
} from "/app/src/components/explorer/helpers/columns";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { useAuthState } from "/app/src/contexts/authentication";

/**
 * Orders component that renders a list of orders with filtering, sorting, pagination, and navigation.
 * @returns {JSX.Element} The rendered orders view.
 */
export default function Orders() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const priorities = [
    { name: "Low (1)", value: 1 },
    { name: "Medium (2)", value: 2 },
    { name: "High (3)", value: 3 },
    { name: "Hot (4)", value: 4 },
  ];
  const navigate = useNavigate();
  const { user } = useAuthState();
  const columnHelper = createColumnHelper<OrderType>();

  const availableColumns = [
    { id: "Info1", label: "Info 1" },
    { id: "modifiedDate", label: "Creation Date" },
    { id: "status", label: "Status" },
    { id: "priority", label: "Priority" },
    { id: "orderLinesCount", label: "Number of Lines" },
  ];

  // Persisted column selections for this explorer. Stored as a JSON string in a Setting
  // with name 'explorerOrdersColumns'. Shape: { order: ["name","id", ...], location: [...] }
  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "order", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "order",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "name":
          return columnHelper.accessor("name", {
            id: "name",
            cell: (info) => info.getValue(),
            header: () => t("translation:name"),
          });
        case "priority":
          return columnHelper.accessor("priority", {
            id: "priority",
            cell: (info) => info.getValue(),
            header: () => t("translation:priority"),
          });
        case "Info1":
          return columnHelper.accessor("Info1", {
            id: "Info1",
            cell: (info) => info.getValue(),
            header: () => t("translation:info1"),
          });
        case "modifiedDate":
          return columnHelper.accessor("modifiedDate", {
            id: "modifiedDate",
            cell: (info) => <DateTime date={info.getValue()} />,
            header: () => t("translation:creation_date"),
          });
        case "status":
          return columnHelper.accessor("status", {
            id: "status",
            cell: (info) => info.getValue(),
            header: () => t("translation:status"),
          });
        case "orderLinesCount":
          return columnHelper.accessor("orderLinesCount", {
            id: "orderLinesCount",
            cell: (info) => info.getValue(),
            header: () => t("translation:order_lines"),
          });
        default:
          return null;
      }
    };

    return buildExplorerColumns(settingValue, "order", makeColumn, "name");
  }, [columnHelper, settingValue, t]);

  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  /**
   * Navigates to a specific order's page.
   * @param {number} orderId - The ID of the order to navigate to.
   */
  const goToOrder = useCallback(
    (orderId: number) => {
      navigate(`/explorer/orders/${orderId}`);
    },
    [navigate],
  );

  /**
   * Updates the priority filter.
   * @param {*} value - The new filter value.
   */
  const priorityFilterChange = useCallback(
    (value) => {
      setFilter(value.target.value);
    },
    [setFilter],
  );

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  const {
    data: orders,
    count: ordersCount,
    isFetching,
  } = usePaginatedData<OrderType>({
    queryKey: ["orders"],
    searchString,
    page,
    pageSize,
    sort,
    service: orderService,
    options: {
      state: "queued",
      priority: filter,
    },
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: ordersCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:orders")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:orders") },
        ]}
      />

      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
          <Select
            aria-label="Filter by priority"
            size="md"
            onChange={priorityFilterChange}
            className="w-[300px]"
            placeholder={t("translation:filter_by_priority")}
            items={[
              { name: t("translation:all"), value: "all" },
              ...priorities,
            ]}
          >
            {(item) => (
              <SelectItem key={item.value.toString()}>{item.name}</SelectItem>
            )}
          </Select>
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <Table
        loading={isFetching}
        rows={orders}
        tableColumns={columns}
        length={ordersCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToOrder}
        emptyText={t("translation:no_orders")}
      />
    </div>
  );
}
